import { inject, Injectable } from '@angular/core';
import { EMPTY, map, Observable } from 'rxjs';

import {
    AggregationTimeScale,
    getDatesBetween,
    getPlatformKeysWithFollowers,
    isNotNil,
    isSameDay,
    MalouMetric,
    PlatformKey,
    StatisticsType,
} from '@malou-io/package-utils';

import { LightRestaurant } from '#modules/restaurants/models/restaurant';
import { AbstractCsvService } from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { InsightsByPlatformByRestaurant } from '#modules/statistics/models/insights';
import { InsightsApiService } from '#modules/statistics/services/insights.api.service';
import { AggregatedStatisticsStore } from '#modules/statistics/stores/aggregated-store/aggregated-statistics.store';
import { DatesAndPeriod } from '#shared/models/index';

interface Data {
    insights: InsightsByPlatformByRestaurant;
    restaurants: LightRestaurant[];
    dates: DatesAndPeriod;
}
interface Options {
    platformKeys: PlatformKey[];
    restaurants: LightRestaurant[];
}

@Injectable()
export class AggregatedFollowersCsvInsightsService extends AbstractCsvService<Data, Options> {
    private readonly _insightsApiService = inject(InsightsApiService);
    private readonly _aggregatedStatisticsStore = inject(AggregatedStatisticsStore);

    protected override _getData$({ restaurants }: Options): Observable<Data> {
        const dates = this._aggregatedStatisticsStore.datesFilter();

        if (!dates.startDate || !dates.endDate) {
            return EMPTY;
        }

        return this._insightsApiService
            .getInsights({
                restaurantIds: restaurants.map((r) => r.id),
                platformKeys: getPlatformKeysWithFollowers(),
                metrics: [MalouMetric.FOLLOWERS],
                aggregators: [AggregationTimeScale.BY_DAY],
                startDate: dates.startDate.toISOString(),
                endDate: dates.endDate.toISOString(),
                statisticsType: StatisticsType.SOCIAL,
            })
            .pipe(
                map((res) => ({
                    insights: res.data,
                    restaurants,
                    dates,
                }))
            );
    }

    protected override _isDataValid(data: Data): boolean {
        return (
            Object.values(data.insights).filter((value) => !value?.facebook?.error && !value?.instagram?.error && !value?.tiktok?.error)
                .length > 0
        );
    }

    protected override _getCsvHeaderRow(): string[] {
        return ['Date', 'Location', 'Location Internal Name', 'Location Address', 'Followers'];
    }

    protected override _getCsvDataRows(data: Data, options: Options): string[][] {
        const { startDate, endDate } = data.dates;
        if (!startDate || !endDate) {
            return [];
        }
        const dates = getDatesBetween(startDate, endDate).filter(isNotNil);
        const { insights, restaurants } = data;

        return dates
            .map((date) =>
                Object.keys(insights)
                    .map((restaurantId) => {
                        const restaurant = restaurants.find((r) => r.id === restaurantId);
                        const restaurantInsights = insights[restaurantId];
                        if (
                            !restaurant ||
                            (restaurantInsights.facebook?.error && restaurantInsights.instagram?.error && restaurantInsights.tiktok?.error)
                        ) {
                            return null;
                        }
                        const localDate = date?.toLocaleDateString();
                        const restaurantName = restaurant.name;
                        const restaurantInternalName = restaurant.internalName ?? '';
                        const restaurantAddress = restaurant.getFullFormattedAddress().replace(/,/g, '');
                        const followers = options.platformKeys
                            .map(
                                (platformKey) =>
                                    restaurantInsights[platformKey]?.by_day?.followers?.find((day) => isSameDay(date, new Date(day.date)))
                                        ?.value ?? 0
                            )
                            .reduce((acc, next) => acc + next, 0);
                        const stringFollowers = followers ? followers.toString() : '-';

                        return [localDate, restaurantName, restaurantInternalName, restaurantAddress, stringFollowers];
                    })
                    .filter(isNotNil)
            )
            .flat();
    }
}
